// GroupOfExpertise.tsx
import React, { useState } from "react";
import { Button, Popconfirm } from "antd";
import { Trash2 } from "lucide-react";
import { useRemoveExpertise } from "../../../../hooks/api/Tutors/UpdateProfile/useRemoveExpertise";

type Subject = {
  id: string;
  subject: string;
  category: "High School" | "Middle School 6-8" | "Elementary K-5";
  createdAt: string;
  updatedAt: string;
};

type GroupOfExpertiseProps = {
  areaOfExpertise: Subject[];
};

const GroupOfExpertise: React.FC<GroupOfExpertiseProps> = ({
  areaOfExpertise,
}) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { mutateAsync } = useRemoveExpertise();

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  };

  const groupedByCategory = areaOfExpertise.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, Subject[]>);

  const categoryOrder: Subject["category"][] = [
    "High School",
    "Middle School 6-8",
    "Elementary K-5",
  ];

  return (
    <div>
      {categoryOrder.map((category) => {
        const subjects = groupedByCategory[category];
        if (!subjects) return null;

        return (
          <div key={category} className="mb-6">
            <h3 className="font-semibold text-lg mb-2 text-gray-800">
              {category}
            </h3>
            <ul className="space-y-2">
              {subjects.map((subject) => (
                <li
                  key={subject.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">
                    {subject.subject}
                  </span>
                  <Popconfirm
                    title="Delete Subject"
                    description="Are you sure you want to remove this subject?"
                    onConfirm={() => handleDelete(subject.id)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      danger
                      size="small"
                      loading={deletingId === subject.id}
                      disabled={deletingId !== null && deletingId !== subject.id}
                      className="flex items-center justify-center"
                    >
                      {!deletingId || deletingId !== subject.id ? (
                        <Trash2 size={16} />
                      ) : null}
                    </Button>
                  </Popconfirm>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default GroupOfExpertise;
